import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, FileText, Building2, Package, HelpCircle, Briefcase, Download, Globe, Users } from "lucide-react";
import { searchSite, groupResults } from "@/searchIndex";

const CATEGORY_ICONS = {
  Pages: Globe,
  Industries: Building2,
  Services: Briefcase,
  Products: Package,
  FAQs: HelpCircle,
  Downloads: Download,
  Projects: FileText,
  Brands: Users,
};

const SearchOverlay = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({});
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResults({});
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const handleSearch = useCallback((value) => {
    setQuery(value);
    if (value.length >= 2) {
      const raw = searchSite(value);
      setResults(groupResults(raw));
    } else {
      setResults({});
    }
  }, []);

  const handleNavigate = (url) => {
    onClose();
    navigate(url);
  };

  const totalResults = Object.values(results).reduce((sum, arr) => sum + arr.length, 0);
  const hasResults = totalResults > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-[#0A0B0D]/90 backdrop-blur-md" onClick={onClose} />

          {/* Search Panel */}
          <motion.div
            className="relative w-full max-w-2xl mx-4 bg-[#121318] border border-white/[0.08] overflow-hidden"
            style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.8), 0 0 40px rgba(139,35,50,0.08)" }}
            initial={{ opacity: 0, y: -20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            data-testid="search-overlay"
          >
            {/* Search Input */}
            <div className="flex items-center gap-4 p-5 border-b border-white/[0.06]">
              <Search size={20} className="text-[#8B2332] flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search services, products, industries, downloads..."
                className="flex-1 bg-transparent text-white text-base font-['Manrope'] outline-none placeholder:text-white/25"
                data-testid="search-input"
              />
              <button onClick={onClose} className="text-white/30 hover:text-white/60 transition-colors p-1" aria-label="Close search">
                <X size={18} />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto">
              {query.length >= 2 && !hasResults && (
                <div className="p-8 text-center">
                  <p className="text-white/30 text-sm">No results found for &ldquo;{query}&rdquo;</p>
                  <p className="text-white/20 text-xs mt-2">Try different keywords or browse our industries</p>
                </div>
              )}

              {hasResults && Object.entries(results).map(([category, items]) => {
                const Icon = CATEGORY_ICONS[category] || Globe;
                return (
                  <div key={category} className="border-b border-white/[0.04] last:border-0">
                    <div className="px-5 pt-4 pb-2 flex items-center gap-2">
                      <Icon size={12} className="text-[#8B2332]" />
                      <span className="text-[#8B2332] text-[10px] font-bold uppercase tracking-[0.2em]">{category}</span>
                      <span className="text-white/15 text-[10px]">({items.length})</span>
                    </div>
                    {items.map((item, i) => (
                      <button
                        key={`${item.url}-${i}`}
                        onClick={() => handleNavigate(item.url)}
                        className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-white/[0.03] transition-colors duration-200 group"
                        data-testid={`search-result-${i}`}
                      >
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white/80 text-sm font-semibold truncate group-hover:text-white transition-colors">{item.title}</h4>
                          <p className="text-white/30 text-xs truncate mt-0.5">{item.desc}</p>
                        </div>
                        <ArrowRight size={12} className="text-white/10 group-hover:text-[#8B2332] transition-colors flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                );
              })}

              {query.length < 2 && (
                <div className="p-6">
                  <p className="text-white/20 text-xs uppercase tracking-[0.15em] mb-4">Quick Links</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: "Mining Solutions", url: "/industries/mining" },
                      { label: "Electrical Services", url: "/services" },
                      { label: "Renewable Energy", url: "/industries/renewable-energy" },
                      { label: "Contact Us", url: "/contact" },
                    ].map((link) => (
                      <button key={link.url} onClick={() => handleNavigate(link.url)} className="text-left px-3 py-2 text-white/40 text-xs hover:text-white/70 hover:bg-white/[0.03] transition-all duration-200">
                        {link.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-5 py-3 border-t border-white/[0.04] flex items-center justify-between">
              <span className="text-white/15 text-[10px]">ESC to close</span>
              {hasResults && <span className="text-white/15 text-[10px]">{totalResults} results</span>}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchOverlay;
